/**
 * 時間前市場 板情報タブ。
 *
 * MarketBoardPlaceholderTab を置換する Phase 2 実装。
 * - 板 (5 エリアグループ × 売買 depth)、連系線空容量、約定ティッカー、コマ情報パネル (AC-BIN-026b)
 * - 48 コマ ribbon (取引中 / GC済 / 開始前 の 3 状態 + 取引停止)
 * - 未開場メッセージ (受渡日前日 17:00 開場)
 * - 受渡日 + 当日/翌日 切替
 * - 自動更新スイッチ + LIVE バッジ + 手動更新ボタン
 * - 自社入札: 既存 ListBiddingIntradayOrders から選択コマでフィルタ。 編集・取消ボタンは
 *   入札一覧と共通 (#10110、 [[IntradayBidRowActions]])
 */
import { useEffect, useMemo, useRef, useState } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { ConnectError } from '@connectrpc/connect'
import { createConnectQueryKey } from '@connectrpc/connect-query'
import { BiddingIntradayMarketSuspenseState } from '@ebalance/proto/bffuser/v1/bidding_intraday_market_model_pb'
import {
  listBiddingInterconnectionCapacities,
  listBiddingMarketContracts,
  listBiddingMarketOrderBook,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_market_service-BiddingIntradayMarketService_connectquery'
import { ProcurementNeedMarket } from '@ebalance/proto/bffuser/v1/supply_demand_gap_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Flex,
  Grid,
  HStack,
  LightModeSemanticColors,
  PrimaryButton,
  Switch,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import {
  FaArrowRotateRightRegular,
  FaClockRegular,
  FaPlusRegular,
} from '@enechain/ecloud-icons'

import {
  useRegisteredIssuerOptions,
  useRegisteredIssuerOptionsPending,
} from '~/components/bidding/common/IssuerSelector'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { FilterMultiSelect } from '~/components/common/FilterMultiSelect'
import { HelpIcon } from '~/components/common/HelpIcon'
import { HintTooltip } from '~/components/common/HintTooltip'
import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import { TABLE_HEADER_BG } from '~/components/common/MasterDataTable'
import {
  stickyActionCellStyle,
  stickyActionHeaderStyle,
  useStickyRightShadow,
} from '~/components/common/MasterDataTable/stickyActionColumn'
import { RegionLoading } from '~/components/common/RegionLoading'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { TradeSideRadioGroup } from '~/components/common/TradeSideRadioGroup'
import { iconSize } from '~/constants/iconSize'
import { biddingQueryKeys } from '~/hooks/bidding/queryKeys'
import {
  type IntradayOrderRow,
  convertListIntradayResponse,
  useListBiddingIntradayOrders,
} from '~/hooks/bidding/useListBiddingIntradayOrders'
import { useProcurementNeedChartData } from '~/hooks/bidding/useProcurementNeedChartData'
import { useBusinessAreaFilter } from '~/hooks/useBusinessAreaFilter'
import { useNowByMinute } from '~/hooks/useNowByMinute'
import { usePersistedFilters } from '~/hooks/usePersistedFilters'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { JEPX_AREA_CODES, convertAreaLabel } from '~/models/area'
import { formatJstTimeHms } from '~/models/dateTime'
import { formatDecimalValue, formatNumber } from '~/models/decimal'
import { formatKomaLabel } from '~/models/komaLabel'
import { useGetApprovalFlowScreen } from '~/pages/AdminApprovalFlow/hooks/useGetApprovalFlowScreen'
import {
  type IntradayBidCreateFormInput,
  IntradayBidCreateModal,
} from '~/pages/IntradayBidding/components/IntradayBidCreateModal'
import {
  IntradayBidRowActions,
  intradayBidRowActionFlags,
} from '~/pages/IntradayBidding/components/IntradayBidRowActions'
import { IntradayBidStatusCell } from '~/pages/IntradayBidding/components/IntradayBidStatusCell'
import {
  IntradayBoardInterconPanel,
  MARKET_PANEL_HEADER_MIN_HEIGHT,
  type PriceCellBid,
} from '~/pages/IntradayBidding/components/IntradayBoardInterconPanel'
import { IntradayNeedsReviewBanner } from '~/pages/IntradayBidding/components/IntradayNeedsReviewBanner'
import { IntradayNeedsReviewChip } from '~/pages/IntradayBidding/components/IntradayNeedsReviewChip'
import { IntradayOrderDetailModal } from '~/pages/IntradayBidding/components/IntradayOrderDetailModal'
import { IntradayWithdrawConfirmDialog } from '~/pages/IntradayBidding/components/IntradayWithdrawConfirmDialog'
import {
  TickerEstimatedAreaCell,
  TickerEstimatedSideBadge,
} from '~/pages/IntradayBidding/components/MarketTickerEstimate'
import { useActiveOrderCounts } from '~/pages/IntradayBidding/hooks/useActiveOrderCounts'
import { useBoardColumnOrder } from '~/pages/IntradayBidding/hooks/useBoardColumnOrder'
import { useIntradayBidCreateSubmit } from '~/pages/IntradayBidding/hooks/useIntradayBidCreateSubmit'
import { useIntradayBoardIntercon } from '~/pages/IntradayBidding/hooks/useIntradayBoardIntercon'
import { useIntradayFeeState } from '~/pages/IntradayBidding/hooks/useIntradayFeeState'
import { useIntradayReturnToDraft } from '~/pages/IntradayBidding/hooks/useIntradayReturnToDraft'
import { useIntradayWithdrawConfirm } from '~/pages/IntradayBidding/hooks/useIntradayWithdrawConfirm'
import { useMarketBoardKoma } from '~/pages/IntradayBidding/hooks/useMarketBoardKoma'
import { useMarketContracts } from '~/pages/IntradayBidding/hooks/useMarketContracts'
import { useMarketNotificationStream } from '~/pages/IntradayBidding/hooks/useMarketNotificationStream'
import { useMarketProducts } from '~/pages/IntradayBidding/hooks/useMarketProducts'
import { useMarketTickerWindow } from '~/pages/IntradayBidding/hooks/useMarketTickerWindow'
import {
  type BoardAreaGroupFilters,
  boardAreaGroupFiltersCodec,
  visibleAreaGroups,
} from '~/pages/IntradayBidding/models/boardAreaGroupFilter'
import {
  convertIntradayBidFromOrder,
  decimalVolumeToMw,
} from '~/pages/IntradayBidding/models/convertIntradayBidsFromOrders'
import { isIntradayApprovalFlowUnresolved } from '~/pages/IntradayBidding/models/intradayApprovalMode'
import { buildIntradayPositionSlots } from '~/pages/IntradayBidding/models/intradayBidAggregation'
import { intradayNeedsReviewCount } from '~/pages/IntradayBidding/models/intradayNeedsReviewCount'
import { areaGroupLabel } from '~/pages/IntradayBidding/models/marketAreaGroupLabel'
import {
  type IntradayGapDirection,
  computeIntradayGapBases,
  computeIntradayGapPercents,
  computeIntradayGapSegments,
  isUncoveredBasisNoteSwitched,
} from '~/pages/IntradayBidding/models/marketBoardGapSummary'
import { MARKET_AREA_GROUPS } from '~/pages/IntradayBidding/models/marketBoardPivot'
import {
  computeContractPriceChanges,
  computeMarketBoardTradeSummary,
  sortContractsLatestFirst,
} from '~/pages/IntradayBidding/models/marketBoardSummary'
import {
  type MarketKomaRibbonState,
  marketKomaRibbonState,
} from '~/pages/IntradayBidding/models/marketKomaRibbonState'
import {
  isMarketClosed,
  marketOpenTimeLabel,
} from '~/pages/IntradayBidding/models/marketOpen'
import { tickerEstimateOf } from '~/pages/IntradayBidding/models/marketTickerEstimate'
import {
  type MyBidsFilters,
  filterMyBidsByAreaAndSide,
  filterMyBidsByKoma,
  myBidsFiltersCodec,
} from '~/pages/IntradayBidding/models/myBidsFilter'
import { ribbonScrollLeftToReveal } from '~/pages/IntradayBidding/models/ribbonScroll'

/**
 * ribbon chip の下線色 (= 3 状態の視覚的区別、 AC-BIN-025)。
 * design `components.css:4584-4586` の `.koma-chip.trading` (positive 下線) /
 * `.koma-chip.upcoming` (border-mid 下線) に対応し、 GC 済は下線を持たず
 * 文字色・背景の減光で区別する (`.koma-chip.gc-closed`)。
 * 色値は共通 ribbon (`components/bidding/common/KomaRibbon.tsx`) と揃える。
 * 選択中 chip は accent の四辺枠を優先するため、下辺の状態色は適用しない
 * (呼び出し側で `isSelected` を判定)。
 */
const ribbonBorderBottomColor = (
  state: MarketKomaRibbonState,
): string | undefined => {
  switch (state) {
    case 'trading':
    case 'imminent':
      return 'green.400'
    case 'pre':
      return 'Border.midEmphasis'
    case 'closed':
      return undefined
  }
}

/**
 * 約定ティッカーの列 (見出しと行で共有)。 design `bidding/intraday-price.html` の `.im-ticker-row` の
 * 列幅 (時刻 72px | 売買 80px | 価格 110px | 約定量 96px | 売りエリア | 買いエリア) を写す。
 * design の「前約定比」 列は未実装、 「自社」 列は約定データ (`BiddingMarketContract`) に自社の
 * 区別が無いため、 配置を変えた #10113 では持たない。
 */
const TICKER_ROW_COLUMNS =
  '72px 80px 110px 96px 96px minmax(120px, 1fr) minmax(120px, 1fr)'

/** ribbon chip の状態ラベル (= tooltip / aria-label の末尾に付ける)。 */
/** 売買方向。 modal の form 値が文字列なので合わせる (1=買 / 2=売)。 */
const BUY_SIDE = '1'
const SELL_SIDE = '2'

/** 板情報のエリアグループ絞り込み (AC-BIN-103) の localStorage key。 全体フィルターの key とは分ける。 */
const BOARD_AREA_GROUP_FILTERS_STORAGE_KEY =
  'ebalance-intraday-board-area-groups'
const MY_BIDS_FILTERS_STORAGE_KEY = 'ebalance-intraday-board-my-bids-filters'

/**
 * 札作成モーダルへ事前入力する値。 コマは選択中のコマ固定。 板の価格欄 (AC-BIN-119) は売買と価格を入れ、
 * 板の見出しの「+ 入札追加」 (クイック入札、 AC-BIN-027 / 028) は売買を入れず、 モーダルの既定の「買」 で開く
 * (売買はモーダルで変えられる)。
 */
type QuickBidPrefill = Partial<
  Pick<IntradayBidCreateFormInput, 'side' | 'priceYenPerKwh'>
>

const RIBBON_STATE_LABEL_KEY: Record<MarketKomaRibbonState, string> = {
  trading: 'page:biddingIntraday.market.ribbon.trading',
  imminent: 'page:biddingIntraday.market.ribbon.imminent',
  closed: 'page:biddingIntraday.market.ribbon.closed',
  pre: 'page:biddingIntraday.market.ribbon.pre',
}

/**
 * 自社過不足の方向ラベル (AC-BIN-026a: 買方向 不足 / 売方向 余剰 / 過不足なし)。
 * `balanced` は need=0 の空メッセージ分岐 (方向・必要量の行自体を出さない) の
 * ため実際には参照されないが、 `IntradayGapDirection` を網羅するために持つ。
 */
const GAP_DIRECTION_LABEL_KEY: Record<IntradayGapDirection, string> = {
  shortage: 'page:biddingIntraday.market.gap.directionShortage',
  surplus: 'page:biddingIntraday.market.gap.directionSurplus',
  balanced: 'page:biddingIntraday.market.gap.directionBalanced',
}

/**
 * 自社過不足の方向カラー。 spec (AC-BIN-026a) は「買方向=強調色A系、 売方向=
 * 強調色B系の使い分けのみ規定し、 具体色は指定しない (デザイナー判断)」ため、
 * 板情報パネルで既に使っている買/売配色 (Text.bid / Text.offer) を流用する。
 */
const gapDirectionColor = (direction: IntradayGapDirection): string => {
  switch (direction) {
    case 'shortage':
      return LightModeSemanticColors.Text.bid.value
    case 'surplus':
      return LightModeSemanticColors.Text.offer.value
    case 'balanced':
      return LightModeSemanticColors.Text.lowEmphasis.value
  }
}

// 入札のみ (下書き等) のハッチ。 濃淡だけでは色覚特性のある人や幅の狭い時に 入札済み と
// 下書き等が分離しないため、 色以外の手掛かりにする (#9069 Des 判断 2026-09-29)。
const GAP_DRAFT_STRIPE =
  'repeating-linear-gradient(45deg, rgba(255,255,255,.85) 0 3px, rgba(255,255,255,0) 3px 6px)'

/**
 * 自社過不足の横棒・凡例の塗り。 入札追加タブのチャートの線の青系 (入札済み・全札) は、
 * 方向色 (買=A 系 / 売=B 系) の意味が混ざるので使わない (#9069 Des 判断 2026-09-29)。
 */
const gapSegmentFills = (
  direction: IntradayGapDirection,
): Record<
  'matched' | 'bidOnlyPlaced' | 'bidOnlyDraft',
  { backgroundColor: string; backgroundImage?: string }
> => {
  const color = gapDirectionColor(direction)
  return {
    matched: { backgroundColor: color },
    bidOnlyPlaced: {
      backgroundColor: `color-mix(in srgb, ${color} 55%, white)`,
    },
    bidOnlyDraft: {
      backgroundColor: `color-mix(in srgb, ${color} 25%, white)`,
      backgroundImage: GAP_DRAFT_STRIPE,
    },
  }
}

/** JS Date (local midnight) を proto Date に変換する。 */
function jsDateToProtoDate(d: Date): ProtoDate {
  return {
    $typeName: 'google.type.Date',
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * 板画面「自分の注文」 一覧の列定義。
 * 揃えを列ごとに持ち、 TH と TD の両方へ同じ値を渡す (位置ベースの分岐にすると
 * 列の並べ替えでヘッダだけがずれる。 2026-08-27 に実際に起きた)。
 * ステータスが先頭なのは全画面共通の列順規約 (2026-08-27 デザイナー判断)。
 */
const MY_BIDS_COLUMNS = [
  {
    key: 'status',
    labelKey: 'page:biddingIntraday.market.myBids.col.status',
    align: 'left',
  },
  {
    key: 'direction',
    labelKey: 'page:biddingIntraday.market.myBids.col.direction',
    // 売/買 は固定値 (enum) 列なので TH/TD とも center
    // (8080 列揃え規約: design/CLAUDE.md「固定値列は TH と合わせて center」)。
    align: 'center',
  },
  {
    key: 'area',
    labelKey: 'page:biddingIntraday.market.myBids.col.area',
    align: 'left',
  },
  {
    key: 'volumeMw',
    labelKey: 'page:biddingIntraday.market.myBids.col.volumeMw',
    align: 'right',
  },
  {
    key: 'priceYenKwh',
    labelKey: 'page:biddingIntraday.market.myBids.col.priceYenKwh',
    align: 'right',
  },
  {
    key: 'matchedVolumeMw',
    labelKey: 'page:biddingIntraday.market.myBids.col.matchedVolumeMw',
    align: 'right',
  },
  {
    key: 'matchedPriceYenKwh',
    labelKey: 'page:biddingIntraday.market.myBids.col.matchedPriceYenKwh',
    align: 'right',
  },
  {
    // 編集・取消ボタン (#10110、 design intraday-price.html `.im-mybids-actions-hd`)。
    // 入札一覧と同じく右揃えで取消の位置を固定する。
    key: 'actions',
    labelKey: 'common:table.actionsLabel',
    align: 'right',
  },
] as const satisfies ReadonlyArray<{
  key: string
  labelKey: string
  // 'center' は固定値 (enum) 列用 (8080 列揃え規約: design/CLAUDE.md
  // 「固定値列は TH と合わせて center、 数値は right、 可変テキストは left」)。
  align: 'left' | 'center' | 'right'
}>

type Props = {
  memberBidDisabledReason?: string
  isReadonly?: boolean
  /**
   * 受渡日の固定値 (= ワークフローケースで開いた場合のみ指定される)。
   *
   * spec は受渡日を page 単位の概念として定義しており (AC-BIN-000a)、 札作成モーダルは
   * その受渡日を読み取り専用で継承する (AC-BIN-004)。 板発注セクションには受渡日
   * セレクタの規定が無いため、 ケース対象日で固定されている間 (AC-WFW-003a) は板も
   * その日にスコープする。 これが無いと全体フィルタの受渡日がそのまま Create の受渡日に
   * なり、 ケース対象外の日の札が作られる。
   */
  lockedDeliveryDate?: Date
  /**
   * AC-BIN-102 全体フィルタの受渡日 (= 入札管理・約定履歴と共有)。 過去日は page が当日に
   * 寄せた実効値 (index.tsx の effectiveTargetDate) を渡す。 `lockedDeliveryDate` が
   * undefined の間の実効値になる。 受渡日を選ぶ UI は本タブには無く、 4 タブ共通の全体の条件
   * ([[IntradayFilterToolbar]]) のカードの中の [[IntradayDeliveryDateControl]] が持つ。
   */
  targetDate: Date
}

/** 接続状態の badge (LIVE / 再接続中 / OFF)。 開閉式フィルターの状況に出す読み取り専用の表示。 */
const StreamPhaseBadge = ({
  streamPhase,
}: {
  streamPhase: ReturnType<typeof useMarketNotificationStream>['phase']
}): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <>
      {/* connection badge: LIVE / 再接続中 / OFF の 3 状態 */}
      {streamPhase === 'connected' ? (
        // eslint-disable-next-line ebalance/no-adhoc-typography -- 接続状態 badge xs/bold (design intraday-price.html:26 .im-live-badge = xs/700 一致。role 不在、バッジ統合トラック defer)
        <Box
          as="span"
          alignItems="center"
          gap={1}
          display="inline-flex"
          padding="1px 6px"
          color="Text.positiveHigh"
          fontSize="xs"
          fontWeight="bold"
          borderRadius="base"
          aria-live="polite"
          backgroundColor="Surface.positiveLow"
        >
          LIVE
        </Box>
      ) : streamPhase !== 'idle' ? (
        // eslint-disable-next-line ebalance/no-adhoc-typography -- 接続状態 badge xs/bold (design intraday-price.html:26 .im-live-badge = xs/700 一致。role 不在、バッジ統合トラック defer)
        <Box
          as="span"
          alignItems="center"
          gap={1}
          display="inline-flex"
          padding="1px 6px"
          color="Text.warningHigh"
          fontSize="xs"
          fontWeight="bold"
          borderRadius="base"
          aria-label={t('page:biddingIntraday.market.reconnectingLabel')}
          aria-live="polite"
          // role の無い span に aria-label は付けられない
          // (axe aria-prohibited-attr)。 見えている「再接続中…」より詳しい
          // 説明を読み上げさせたいので、 ラベルを消すのではなく role を足す。
          // 状態の実況なので `status` (= aria-live polite を含意) が正。
          backgroundColor="Surface.warningLow"
          role="status"
        >
          {t('page:biddingIntraday.market.reconnecting')}
        </Box>
      ) : (
        // eslint-disable-next-line ebalance/no-adhoc-typography -- 接続状態 badge xs/bold (design intraday-price.html:26 .im-live-badge = xs/700 一致。role 不在、バッジ統合トラック defer)
        <Box
          as="span"
          alignItems="center"
          gap={1}
          display="inline-flex"
          padding="1px 6px"
          color="Text.lowEmphasis"
          fontSize="xs"
          fontWeight="bold"
          borderRadius="base"
          aria-live="polite"
          backgroundColor="Surface.secondary"
        >
          OFF
        </Box>
      )}
    </>
  )
}

export const MarketBoardTab = ({
  memberBidDisabledReason,
  isReadonly = false,
  lockedDeliveryDate,
  targetDate,
}: Props): React.ReactElement => {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()

  // ─ date selection ──────────────────────────────────────────────────────
  // GC 状態はコマ境界 (30 分) をまたぐと変わるため、 render 時の `new Date()` では
  // なく分境界に同期した共有 hook を使う (= ribbon の 3 状態が stale にならない)。
  const now = useNowByMinute()

  // AC-BIN-102: 受渡日は全体フィルタを他画面と共有し、 板も共有値の日をそのまま出す
  // (翌日より先は市場未開場の表示になる)。 受渡日を選ぶのは全体の条件のカードの中の
  // [[IntradayDeliveryDateControl]] (4 タブ共通) で、 板は読むだけ。 過去日を
  // 当日に寄せた「実効の受渡日」 は page (index.tsx の effectiveTargetDate) が 1 回だけ
  // 決めて渡す (ヘッダーと板が別の日を指さないため。 0 時を跨いだ時の繰り上げも page の
  // 分 tick が担う)。
  const selectedDeliveryDate = lockedDeliveryDate ?? targetDate
  const deliveryProtoDate = jsDateToProtoDate(selectedDeliveryDate)

  // ─ koma selection ─────────────────────────────────────────────────────
  // 選んだコマはタブの往復・再訪をまたいで保持し、 保持が使えない時は入札できる先頭の
  // コマ (issue #10107 / #11101、 [[useMarketBoardKoma]])。
  const [selectedTimeCode, setSelectedTimeCode] =
    useMarketBoardKoma(selectedDeliveryDate)

  // 選択中のコマの chip がリボンの横スクロールの外に隠れていたら、 見える位置まで
  // スクロールする (復元したコマ・開いた時や受渡日の変更で入札できる先頭のコマに
  // なった時。 見えている chip をクリックした時は動かさない。
  // [[ribbonScrollLeftToReveal]])。
  const ribbonScrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const wrap = ribbonScrollRef.current
    const chip = wrap?.querySelector<HTMLElement>(
      `[data-koma="${selectedTimeCode}"]`,
    )
    if (wrap == null || chip == null) return
    const left = ribbonScrollLeftToReveal({
      chipLeft:
        chip.getBoundingClientRect().left -
        wrap.getBoundingClientRect().left +
        wrap.scrollLeft,
      chipWidth: chip.getBoundingClientRect().width,
      scrollLeft: wrap.scrollLeft,
      viewWidth: wrap.clientWidth,
    })
    if (left !== undefined) wrap.scrollTo({ left })
  }, [selectedTimeCode])

  // ─ auto refresh ───────────────────────────────────────────────────────
  const [autoRefresh, setAutoRefresh] = useState(true)

  // ─ stream subscription ────────────────────────────────────────────────
  const { phase: streamPhase, streamStopped } = useMarketNotificationStream(
    autoRefresh,
    selectedDeliveryDate,
    selectedTimeCode,
  )

  // ─ market closed gate ─────────────────────────────────────────────────
  const marketClosed = isMarketClosed(now, selectedDeliveryDate)
  // AC-SPS-033: 売買列の左右は個人設定 (AC-SPS-032)。 初回は取得が決着するまで板を描かない (D8)。
  const { columnOrder } = useBoardColumnOrder()
  // AC-BIN-103: 板情報のエリアグループ絞り込み。 板発注タブの中だけで持ち、 全体フィルター
  // (AC-BIN-102) には反映しない。 タブを切り替えると本 component は unmount されるので、
  // 全体フィルターとは別の key で localStorage に持つ (AC-SC-031、 design `boardGroupSel`)。
  const { filters: boardFilters, updateFilters: updateBoardFilters } =
    usePersistedFilters<BoardAreaGroupFilters>({
      storageKey: BOARD_AREA_GROUP_FILTERS_STORAGE_KEY,
      codec: boardAreaGroupFiltersCodec,
    })
  const boardAreaGroups = useMemo(
    () => visibleAreaGroups(boardFilters.areaGroups),
    [boardFilters.areaGroups],
  )
  // AC-BIN-103: 自社入札テーブルのエリア・売買区分の絞り込み。 板のエリアグループと同じく板発注タブの中だけで持つ
  // (design `intraday-price.html` の `myBidsAreaSel` / `myBidsSide`)。 エリアの選択肢は全体フィルターと同じく
  // 業務エリア (conventions.md「業務エリアフィルタの原則」)。 業務エリア外になった選択は使わない
  const { filters: myBidsFilters, updateFilters: updateMyBidsFilters } =
    usePersistedFilters<MyBidsFilters>({
      storageKey: MY_BIDS_FILTERS_STORAGE_KEY,
      codec: myBidsFiltersCodec,
    })
  const { selectableAreaCodes: myBidsAreaOptions } =
    useBusinessAreaFilter(JEPX_AREA_CODES)
  const effectiveMyBidsFilters = useMemo<MyBidsFilters>(
    () => ({
      ...myBidsFilters,
      areas: myBidsFilters.areas.filter((area) =>
        myBidsAreaOptions.includes(area),
      ),
    }),
    [myBidsFilters, myBidsAreaOptions],
  )
  const marketOpenTime = marketOpenTimeLabel(
    selectedDeliveryDate,
    i18n.language,
  )

  // ─ data fetching ──────────────────────────────────────────────────────
  // backend gRPC 未接続 (= port-forward 切れ等) のときは error を画面に出さず、
  // 各セクションをローディング (Spinner) 表示にとどめ、 詳細は console.error に出す。
  // 板情報・連系線空容量の取得 + 整形は入札追加タブ (BidCreateTab) と共通の hook に
  // 集約している (useIntradayBoardIntercon の JSDoc 参照)。
  const {
    boardRows,
    isLoadingBook,
    isErrorBook,
    errorBook,
    interconLines,
    interconUpdatedAt,
    isLoadingIntercon,
    isErrorIntercon,
    errorIntercon,
  } = useIntradayBoardIntercon(
    deliveryProtoDate,
    selectedTimeCode,
    !marketClosed,
    boardAreaGroups,
  )

  const {
    data: contractsData,
    isLoading: isLoadingContracts,
    isError: isErrorContracts,
    error: errorContracts,
  } = useMarketContracts(
    deliveryProtoDate,
    selectedTimeCode,
    !marketClosed,
    autoRefresh,
  )
  // 取り直しに失敗しても取得済みの約定は出し続ける。 失敗として扱うのは一度も取得できていない間だけ (#9491)
  const isErrorContractsWithoutData =
    isErrorContracts && contractsData === undefined

  const { data: productsData, error: errorProducts } = useMarketProducts(
    deliveryProtoDate,
    !marketClosed,
  )

  // ribbon の件数 = 自社の入札中の札の件数 (AC-BIN-025)。 `countsByKoma` が undefined の
  // 間 (件数の query の error は console 出力のみで画面に出ない) は 「入札中の札なし」 と
  // 断定せず、 件数セグメントごと落とす。
  const {
    countsByKoma: activeOrderCountsByKoma,
    error: errorActiveOrderCounts,
  } = useActiveOrderCounts(deliveryProtoDate, {
    enabled: !marketClosed,
    // 取得できなくても全画面エラーにせず、 件数セグメントだけを落とす。
    throwOnError: false,
  })

  // error は画面表示せず console に出す (= 接続不可時の一次切り分け用)。
  // ユーザー要望により接続不可は全画面エラーにせず console.error のみ。
  useEffect(() => {
    const failures: Array<[string, ConnectError | null]> = [
      ['板情報', errorBook],
      ['連系線空容量', errorIntercon],
      ['約定履歴', errorContracts],
      ['コマ取引状態', errorProducts],
      ['コマ別の自社の入札中の札の件数', errorActiveOrderCounts],
    ]
    for (const [label, err] of failures) {
      if (err !== null) {
        // eslint-disable-next-line no-console -- 接続不可時の一次切り分け用に console 出力する
        console.error(`[時間前市場] ${label}の取得に失敗:`, err.message)
      }
    }
  }, [
    errorBook,
    errorIntercon,
    errorContracts,
    errorProducts,
    errorActiveOrderCounts,
  ])

  // ─ my bids ────────────────────────────────────────────────────────────
  // 選択中の受渡日 (deliveryProtoDate) で絞る。 絞らないと同じコマ番号の別受渡日の
  // 注文まで myBids (= 自社入札テーブル AC-BIN-032・自社過不足 AC-BIN-026a の入力)
  // に混在し、 過不足計算の cover/executed が誤った値になる (bot 指摘、 2026-09-15)。
  const { data: myOrdersData } = useListBiddingIntradayOrders(deliveryProtoDate)
  const allOrders = useMemo(
    () =>
      myOrdersData !== undefined
        ? convertListIntradayResponse(myOrdersData)
        : [],
    [myOrdersData],
  )
  // AC-BIN-127: 警告の件数は受渡日全体 (選択中のコマに依らない)。
  const needsReviewCount = useMemo(
    () => intradayNeedsReviewCount(allOrders, selectedDeliveryDate),
    [allOrders, selectedDeliveryDate],
  )
  const myBids = useMemo(
    () => filterMyBidsByKoma(allOrders, selectedTimeCode),
    [allOrders, selectedTimeCode],
  )
  const myBidEntries = useMemo(
    () =>
      myBids.map((order) => {
        const row = convertIntradayBidFromOrder(order)
        // 行の編集・取消ボタン (#10110)。 編集は GC を跨いだら消すので分単位の now で引き直す。
        return { order, row, actionFlags: intradayBidRowActionFlags(row, now) }
      }),
    [myBids, now],
  )
  // 自社入札テーブルに出す行 (AC-BIN-103 の絞り込み)。 自社過不足 (AC-BIN-026a) は全エリア合算のまま
  // myBids を使うので、 絞るのはテーブルだけ
  const shownMyBidEntries = useMemo(() => {
    const shown = new Set(
      filterMyBidsByAreaAndSide(myBids, effectiveMyBidsFilters),
    )
    return myBidEntries.filter((entry) => shown.has(entry.order))
  }, [myBids, myBidEntries, effectiveMyBidsFilters])
  // 自社入札の行の編集 → 詳細・編集モーダル (AC-007)、 取消 → 取消確認モーダル (AC-008)。
  // 入札一覧と同じモーダルを開く (#10110)。
  const [detailOrder, setDetailOrder] = useState<IntradayOrderRow | undefined>(
    undefined,
  )
  const withdrawConfirm = useIntradayWithdrawConfirm()
  const returnToDraft = useIntradayReturnToDraft()

  // ─ 自社過不足 (AC-BIN-026a) ────────────────────────────────────────────
  // need (調達必要量) は全 9 エリア合算で取得する。 板発注画面は全体フィルターの
  // エリアが無効化される (AC-BIN-103) ため、 上部フィルタの選択状態には依存せず
  // このタブ内で固定取得する (#7144 論点4 決定B、 2026-09-07 PdM 判断:
  // 板発注だけエリア合算を許容する。 板は板を監視しながらコマ単位で概況を掴む
  // 表示で、 発注数量はコマ・エリアを確定させた札作成モーダルで決めるため)。
  const boardGapAllAreas = useMemo(() => new Set(JEPX_AREA_CODES), [])
  // need 取得に失敗した時 (needUnavailable) は needByKoma が undefined になり、
  // 下の need=0 フォールバックで direction が 'balanced' になるため、 そのままでは
  // 「このコマは過不足ゼロ」 という業務事実と取れる表示になってしまう。 取得失敗は
  // 過不足ゼロと区別して注記を出す (#8153 EN 暫定判断 2026-09-19、 PdM 事後確認は
  // #8404。 入札追加タブのチャート〈AC-BIN-012b〉の need 取得失敗注記と同じ扱い)。
  const { needByKoma: boardNeedByKoma, needUnavailable: boardNeedUnavailable } =
    useProcurementNeedChartData(
      selectedDeliveryDate,
      boardGapAllAreas,
      ProcurementNeedMarket.INTRADAY,
    )
  // 読込中 (初期表示・受渡日の切り替え直後) も need が無いので、 過不足ゼロと区別する。
  const boardNeedLoading =
    !boardNeedUnavailable && boardNeedByKoma === undefined
  // need / cover (入札時カバー量) / executed (約定量) は入札追加タブのチャートと同じ
  // `buildIntradayPositionSlots` で全エリア合算する (AC-BIN-012a の net 充当式・クランプ
  // なし、 #8575 の EN 判断 2026-09-28)。 myBids は既に選択中コマに絞り込み済み。
  // 自社入札の表の操作列 (編集・取消) を右端に固定する影の状態。
  const { wrapRef: myBidsWrapRef, stickyShadow: myBidsStickyShadow } =
    useStickyRightShadow([shownMyBidEntries])
  const boardGapBids = useMemo(
    () => myBidEntries.map(({ row }) => row),
    [myBidEntries],
  )
  const {
    segments: boardGapSegments,
    bases: boardGapBases,
    uncoveredBasisSwitched: boardUncoveredBasisSwitched,
  } = useMemo(() => {
    const slot = buildIntradayPositionSlots(boardGapBids, boardNeedByKoma)[
      selectedTimeCode - 1
    ]
    const input = {
      needMw: slot?.needMw ?? 0,
      coverSubmittedMw: slot?.coverSubmittedMw ?? 0,
      coverMw: slot?.coverMw ?? 0,
      executedMw: slot?.executedMw ?? 0,
    }
    return {
      segments: computeIntradayGapSegments(input),
      bases: computeIntradayGapBases(input),
      uncoveredBasisSwitched: isUncoveredBasisNoteSwitched(input),
    }
  }, [boardGapBids, boardNeedByKoma, selectedTimeCode])
  // セグメント内数値ラベルの表示可否判定 (> 12% のときだけ表示、 AC-BIN-026a) に使う
  // 幅比率。 4 セグメント横棒グラフの width と共通の値を使う。
  const boardGapPercents = useMemo(
    () => computeIntradayGapPercents(boardGapSegments),
    [boardGapSegments],
  )
  const boardGapFills = gapSegmentFills(boardGapSegments.direction)
  // 横棒と凡例は、 確度の高い順に並べる (#9069 Des 判断 2026-09-29)。
  const boardGapRows = [
    {
      key: 'matched',
      legendKey: 'page:biddingIntraday.market.gap.legendMatched',
      mw: boardGapSegments.matchedMw,
      pct: boardGapPercents.matchedPct,
      fill: boardGapFills.matched,
      labelColor: 'white',
    },
    {
      key: 'bidOnlyPlaced',
      legendKey: 'page:biddingIntraday.market.gap.legendBidOnlyPlaced',
      mw: boardGapSegments.bidOnlyPlacedMw,
      pct: boardGapPercents.bidOnlyPlacedPct,
      fill: boardGapFills.bidOnlyPlaced,
      labelColor: 'Text.highEmphasis',
    },
    {
      key: 'bidOnlyDraft',
      legendKey: 'page:biddingIntraday.market.gap.legendBidOnlyDraft',
      mw: boardGapSegments.bidOnlyDraftMw,
      pct: boardGapPercents.bidOnlyDraftPct,
      fill: boardGapFills.bidOnlyDraft,
      labelColor: 'Text.highEmphasis',
    },
    {
      key: 'uncovered',
      legendKey: 'page:biddingIntraday.market.gap.legendUncovered',
      mw: boardGapSegments.uncoveredMw,
      pct: boardGapPercents.uncoveredPct,
      fill: { backgroundColor: 'Surface.tertiary' },
      labelColor: 'Text.midEmphasis',
    },
  ] as const
  const boardUncoveredBasisNote = boardUncoveredBasisSwitched
    ? t('page:biddingIntraday.market.gap.uncoveredBasisNoteSwitched', {
        value: formatNumber(boardGapBases.all.uncoveredMw, 1),
      })
    : t('page:biddingIntraday.market.gap.uncoveredBasisNote', {
        value: formatNumber(boardGapBases.submitted.uncoveredMw, 1),
      })
  const boardGapBarLabel = t('page:biddingIntraday.market.gap.barAriaLabel', {
    need: formatNumber(boardGapSegments.needAbsMw, 1),
    matched: formatNumber(boardGapSegments.matchedMw, 1),
    submittedBidOnly: formatNumber(boardGapBases.submitted.bidOnlyMw, 1),
    submittedUncovered: formatNumber(boardGapBases.submitted.uncoveredMw, 1),
    allBidOnly: formatNumber(boardGapBases.all.bidOnlyMw, 1),
    allUncovered: formatNumber(boardGapBases.all.uncoveredMw, 1),
  })

  // ─ quick bid (AC-BIN-027 / 028 / 029) ─────────────────────────────────
  // modal の form 値は文字列 (`IntradayBidCreateFormInput`)。 1=買 / 2=売。
  const [quickBidOpen, setQuickBidOpen] = useState(false)
  // 開くたびに丸ごと置き換える (価格セルから開いた時だけ価格が入る、 #9589)。
  const [quickBidPrefill, setQuickBidPrefill] = useState<QuickBidPrefill>({})
  // 選択中コマが取引中の間だけ出す。 `closed` (GC済) / `pre` (開始前) は AC-BIN-029
  // で非表示。 `imminent` は締切間近だがまだ取引中なので出す
  // (確定時の GC 再判定は useIntradayBidCreateSubmit 側が持つ)。
  const selectedKomaState = marketKomaRibbonState(
    now,
    selectedDeliveryDate,
    selectedTimeCode,
  )
  const quickBidAvailable =
    !marketClosed &&
    (selectedKomaState === 'trading' || selectedKomaState === 'imminent')
  const submitQuickBid = useIntradayBidCreateSubmit({
    targetDate: selectedDeliveryDate,
    targetDateFilter: deliveryProtoDate,
  })
  // AC-BIN-004b: 札作成モーダルは開いた時に手数料自動考慮の状態を読むので、 開く前から取得しておく。
  const feeState = useIntradayFeeState()
  const quickBidDefaultValues = useMemo(
    () => ({ ...quickBidPrefill, timeCode: String(selectedTimeCode) }),
    [quickBidPrefill, selectedTimeCode],
  )
  // クイック入札ボタンと価格欄で共通の、 札作成モーダルを開ける条件。 承認フロー設定が
  // 読めていない間 (取得中・取得失敗) は開かせない (AC-BIN-113、 #8313)。
  const {
    data: approvalFlowScreenData,
    isLoading: isApprovalFlowLoading,
    isError: isApprovalFlowError,
  } = useGetApprovalFlowScreen({ throwOnError: false })
  // 読み取り専用 (AC-WFW-014) でクイック入札を非活性にした時の理由。承認フロー未取得など
  // 画面固有の理由より優先して単独で出す。
  const { readonlyReason } = useWorkflowLockReasons()
  const canOpenBidCreate =
    memberBidDisabledReason === undefined &&
    quickBidAvailable &&
    !isReadonly &&
    !isIntradayApprovalFlowUnresolved({
      processes: approvalFlowScreenData?.processes,
      isLoading: isApprovalFlowLoading,
      isError: isApprovalFlowError,
    })
  const openQuickBid = (prefill: QuickBidPrefill): void => {
    if (!canOpenBidCreate) return
    setQuickBidPrefill(prefill)
    setQuickBidOpen(true)
  }
  // 価格欄は、 さらに発注主体区分が 1 件以上確定している間だけ押せる (0 件・取得中・取得失敗は
  // 押せない。 AC-BIN-056、 #9589)。 区分は画面上部・入札追加タブと同じ「登録済み区分」
  // (受渡日に依らない) で数える。 受渡日の有効な登録エリアが無い区分も選べ、 モーダルの空状態で案内する。
  const issuerOptions = useRegisteredIssuerOptions(selectedDeliveryDate)
  const canBidFromPriceCell = canOpenBidCreate && issuerOptions.length > 0
  // 区分 0 件だけが押せない理由の時に AC-BIN-056 の文言を出す (AC-BIN-119)。 取得中は 0 件と
  // 断定しない。 他の条件でも押せない時は、 区分を直しても押せないので出さない。
  const isIssuerOptionsPending =
    useRegisteredIssuerOptionsPending(selectedDeliveryDate)
  const noIssuerReason =
    canOpenBidCreate && !isIssuerOptionsPending && issuerOptions.length === 0
      ? t('page:biddingCommon.issuerSelector.noAccount')
      : undefined

  // 板情報パネルの見出し 3 指標 (AC-BIN-026: 最終約定価格 / 約定量 / 約定件数)。
  // 約定ティッカーと同一データ (contractsData) から算出し、 追加の RPC は発生させない。
  const boardTradeSummary = useMemo(
    () => computeMarketBoardTradeSummary(contractsData?.contracts ?? []),
    [contractsData],
  )

  // 約定ティッカーは新しい順に 100 件ずつ描画する (#9581)。 取得と上の見出し指標は全件のまま。
  const sortedContracts = useMemo(
    () => sortContractsLatestFirst(contractsData?.contracts ?? []),
    [contractsData],
  )
  // 前約定比変化 (AC-BIN-031)。 描画は 100 件ずつでも、 差は全件の並びで取る。
  const tickerPriceChanges = useMemo(
    () => computeContractPriceChanges(sortedContracts),
    [sortedContracts],
  )
  const tickerResetKey = `${deliveryProtoDate.year}-${deliveryProtoDate.month}-${deliveryProtoDate.day}-${selectedTimeCode}`
  const { visibleItems: tickerContracts, onScroll: onTickerScroll } =
    useMarketTickerWindow(sortedContracts, tickerResetKey)

  // ─ koma product map ───────────────────────────────────────────────────
  const productMap = useMemo(() => {
    const map = new Map<number, BiddingIntradayMarketSuspenseState>()
    for (const p of productsData?.products ?? []) {
      map.set(p.timeCode, p.suspenseState)
    }
    return map
  }, [productsData])

  // ─ manual refresh ─────────────────────────────────────────────────────
  const handleManualRefresh = (): void => {
    void queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listBiddingMarketOrderBook,
        cardinality: 'finite',
        input: { deliveryDate: deliveryProtoDate, timeCode: selectedTimeCode },
      }),
    })
    void queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listBiddingMarketContracts,
        cardinality: 'finite',
        input: { deliveryDate: deliveryProtoDate, timeCode: selectedTimeCode },
      }),
    })
    void queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listBiddingInterconnectionCapacities,
        cardinality: 'finite',
        input: { deliveryDate: deliveryProtoDate, timeCode: selectedTimeCode },
      }),
    })
    // ribbon の件数は受渡日単位 (48 コマ分を 1 query で持つ) なので、 選択コマ
    // ではなく件数の RPC ごと invalidate する。
    void queryClient.invalidateQueries({
      queryKey: biddingQueryKeys.intradayActiveOrderCounts,
    })
  }

  // 約定ティッカー (板発注タブのみ、 AC-BIN-031)。 板 | 連系線 の下に全幅で置く (#10113)。
  // prop に JSX を直書きすると lint (no-adhoc-typography) が子孫の fontSize と fontWeight を
  // 1 要素の手書きとして数えるため、 変数に切り出して渡す。
  const tickerPanel = (
    <Box
      flexDirection="column"
      display="flex"
      overflow="hidden"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="md"
      backgroundColor="Surface.primary"
    >
      {/* パネルの見出しの地は primary。 table-header の地は列見出しの行だけが持つ
          (8080 `bidding/intraday-price.html` の `.im-panel-header` / `.im-ticker-head`、
          2026-10-09 ユーザー指示) */}
      {/* 高さは板・自社入札の見出しにそろえる (MARKET_PANEL_HEADER_MIN_HEIGHT、 2026-11-02 ユーザー指示) */}
      <Box
        alignItems="center"
        display="flex"
        minHeight={MARKET_PANEL_HEADER_MIN_HEIGHT}
        padding="0.4rem 0.65rem"
        borderColor="Border.lowEmphasis"
        borderBottomWidth="1px"
        backgroundColor="Surface.primary"
      >
        {/* 推定値の注記 (AC-BIN-031) は見出しの右の ? の tooltip に置く (8080 `bidding/intraday-price.html` の
            `.im-ticker-title` + `.head-info`、 2026-10-08 ユーザー指示。 2026-10-30 ユーザー指示「8080 準拠」で
            dev も見出しの下の常時の行をやめた。 常時の行は約定が読める行数を削る) */}
        <HStack gap={1}>
          <Text textStyle="subSectionTitle" color="Text.highEmphasis">
            {t('page:biddingIntraday.market.ticker.title')}
          </Text>
          <HelpIcon
            ariaLabel={t('page:biddingIntraday.market.ticker.estimateNote')}
            content={t('page:biddingIntraday.market.ticker.estimateNote')}
          />
        </HStack>
      </Box>
      {/* 受渡日・コマを変えたらスクロール位置も先頭に戻すため、 key で作り直す (#9581)。
          高さは design の .im-ticker-list (max-height 236px) に合わせる */}
      <RegionLoading
        isLoading={isLoadingContracts || isErrorContractsWithoutData}
      >
        <Box
          key={tickerResetKey}
          overflow="auto"
          maxHeight="236px"
          onScroll={onTickerScroll}
        >
          {isLoadingContracts ||
          isErrorContractsWithoutData ? null : tickerContracts.length === 0 ? (
            <Box padding={4} textAlign="center">
              <Text color="Text.midEmphasis" fontSize="sm">
                {t('page:biddingIntraday.market.ticker.empty')}
              </Text>
            </Box>
          ) : (
            <>
              {/* 列見出し (#9583、 design `bidding/intraday-price.html` の .im-ticker-head)。 行と同じ列。
                スクロール領域の中に sticky で置く: 外に置くとスクロールバーの幅だけ見出しの右端が
                値の右端からずれる (Windows の常時表示で 15px)。
                list の子は listitem に限られるので、 見出しは list の外 (兄弟) に置く。 */}
              <Grid
                position="sticky"
                zIndex={1}
                top={0}
                alignItems="center"
                gridColumnGap={3}
                gridTemplateColumns={TICKER_ROW_COLUMNS}
                minWidth="fit-content"
                padding="0.3rem 0.65rem"
                borderColor="Border.lowEmphasis"
                borderBottomWidth="1px"
                backgroundColor="Surface.tableHeader"
              >
                <Text
                  textStyle="tableHeader"
                  color="Text.midEmphasis"
                  whiteSpace="nowrap"
                >
                  {t('page:biddingIntraday.market.ticker.headerTime')}
                </Text>
                <Text
                  textStyle="tableHeader"
                  color="Text.midEmphasis"
                  whiteSpace="nowrap"
                >
                  {t('page:biddingIntraday.market.ticker.headerSideEstimated')}
                </Text>
                <Text
                  textStyle="tableHeader"
                  color="Text.midEmphasis"
                  textAlign="right"
                  whiteSpace="nowrap"
                >
                  {t('page:biddingIntraday.market.ticker.headerPrice')}
                </Text>
                <Text
                  textStyle="tableHeader"
                  color="Text.midEmphasis"
                  textAlign="right"
                  whiteSpace="nowrap"
                >
                  {t('page:biddingIntraday.market.ticker.headerVolume')}
                </Text>
                <Text
                  textStyle="tableHeader"
                  color="Text.midEmphasis"
                  textAlign="right"
                  whiteSpace="nowrap"
                >
                  {t('page:biddingIntraday.market.ticker.headerPriceChange')}
                </Text>
                <Text
                  textStyle="tableHeader"
                  color="Text.midEmphasis"
                  whiteSpace="nowrap"
                >
                  {t('page:biddingIntraday.market.ticker.sellAreaEstimated')}
                </Text>
                <Text
                  textStyle="tableHeader"
                  color="Text.midEmphasis"
                  whiteSpace="nowrap"
                >
                  {t('page:biddingIntraday.market.ticker.buyAreaEstimated')}
                </Text>
              </Grid>
              <Box
                minWidth="fit-content"
                aria-label={t('page:biddingIntraday.market.ticker.title')}
                role="list"
              >
                {tickerContracts.map((contract, idx) => {
                  const time =
                    contract.contractedAt !== undefined
                      ? formatJstTimeHms(timestampDate(contract.contractedAt))
                      : '—'
                  const estimate = tickerEstimateOf(contract)
                  const priceChange = tickerPriceChanges[idx]
                  // 全幅になったので design と同じく 1 約定 = 1 行の表にする (#10113)
                  return (
                    <Grid
                      key={idx}
                      alignItems="center"
                      gridColumnGap={3}
                      gridTemplateColumns={TICKER_ROW_COLUMNS}
                      padding="0.3rem 0.65rem"
                      fontSize="md"
                      borderColor="Border.lowEmphasis"
                      borderBottomWidth="1px"
                      _hover={{ backgroundColor: 'Surface.secondary' }}
                      role="listitem"
                    >
                      <Text
                        color="Text.midEmphasis"
                        whiteSpace="nowrap"
                        css={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {time}
                      </Text>
                      <Box minWidth={0}>
                        <TickerEstimatedSideBadge side={estimate.side} />
                      </Box>
                      {/* 価格の単位 (円/kWh、 AC-BIN-031 / AC-BIN-043) は列見出しに出す (#9583)。
                        design の ticker と同じく、 行には約定量の MW だけを添える。 */}
                      <Text
                        color="Text.highEmphasis"
                        fontWeight="semibold"
                        textAlign="right"
                        whiteSpace="nowrap"
                        css={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {formatDecimalValue(contract.price?.value, 2)}
                      </Text>
                      <Text
                        color="Text.midEmphasis"
                        textAlign="right"
                        whiteSpace="nowrap"
                        css={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        <Text as="span">
                          {formatDecimalValue(contract.volume?.value, 1)}
                        </Text>{' '}
                        <Text as="span">
                          {t('page:biddingIntraday.market.ticker.unitVolume')}
                        </Text>
                      </Text>
                      {/* 前約定比変化 (AC-BIN-031、 design `.im-ticker-delta`)。 上げは positive、
                        下げは negative の色。 変化なしと最も古い約定は「—」。 */}
                      <Text
                        color={
                          priceChange === undefined || priceChange === 0
                            ? 'Text.midEmphasis'
                            : priceChange > 0
                              ? 'Text.positiveHigh'
                              : 'Text.negativeHigh'
                        }
                        textAlign="right"
                        whiteSpace="nowrap"
                        css={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {priceChange === undefined || priceChange === 0
                          ? '—'
                          : priceChange > 0
                            ? `+${priceChange.toFixed(2)}`
                            : priceChange.toFixed(2)}
                      </Text>
                      <TickerEstimatedAreaCell
                        estimate={estimate}
                        side="sell"
                      />
                      <TickerEstimatedAreaCell estimate={estimate} side="buy" />
                    </Grid>
                  )
                })}
              </Box>
            </>
          )}
        </Box>
      </RegionLoading>
    </Box>
  )

  return (
    <VStack align="stretch" gap={3} minHeight="600px">
      {/* 承認フロー設定の取得失敗案内 (AC-BIN-113、 #8313)。 取得中は出さない。 */}
      {isApprovalFlowError && (
        <Alert
          description={t(
            'page:biddingIntraday.bidCreate.approvalFlowUnavailableBody',
          )}
          status="error"
          title={t(
            'page:biddingIntraday.bidCreate.approvalFlowUnavailableTitle',
          )}
        />
      )}
      {/* ── ribbon row ─────────────────────────────────────────────── */}
      <Box
        padding={3}
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="lg"
        backgroundColor="Surface.primary"
      >
        {/* 開閉式にせず、 プロトタイプ (Vercel) の intraday-price.html `.im-ribbon-row` に合わせて
            左に自動更新、 右に 48 コマのリボンを 1 本の枠に並べる (2026-10-26 ユーザー指示「板発注 >
            この部分は、 開閉式にせず、 8080 の UI に準拠」)。 受渡日は全体の条件のカードにあるので
            ここには置かない。 全体フィルターのエリア・売買区分も出さない (AC-BIN-103 / AC-BIN-131)。 */}
        <Flex alignItems="center" gap={4}>
          {/* left: 自動更新 (ラベル + スイッチ + 接続状態 + オフの時だけ今すぐ更新) */}
          <HStack flexWrap="wrap" flexShrink={0} gap={2}>
            <Text
              textStyle="filterLabel"
              color="Text.highEmphasis"
              whiteSpace="nowrap"
            >
              {t('page:biddingIntraday.market.autoRefresh')}
            </Text>
            <Switch
              aria-label={t('page:biddingIntraday.market.autoRefreshLabel')}
              checked={autoRefresh}
              id="im-auto-refresh"
              onCheckedChange={(details: { checked: boolean }) => {
                setAutoRefresh(details.checked)
                // OFF の間は書き換えないので、 ON に戻した時点の最新を出す (AC-BIN-031、 #9491)
                if (details.checked) handleManualRefresh()
              }}
            />
            <StreamPhaseBadge streamPhase={streamPhase} />
            {!autoRefresh && (
              // DS IconButton に tertiary は無いので outline に tertiary の見た目を当てる
              // (プロトタイプ `intraday-price.html` の `.im-refresh-btn` = 枠 1px border-mid /
              // 地 primary / 文字 mid。 枠は button recipe の outline が持つ)。
              <IconOnlyButton
                color="Text.midEmphasis"
                backgroundColor="Surface.primary"
                label={t('page:biddingIntraday.market.refreshNow')}
                onClick={handleManualRefresh}
                showArrow
                size="sm"
                variant="outline"
              >
                <FaArrowRotateRightRegular aria-hidden="true" />
              </IconOnlyButton>
            )}
          </HStack>
          {/* right: 48 koma ribbon */}
          <Box ref={ribbonScrollRef} flex={1} overflowX="auto" minWidth={0}>
            <Flex flexWrap="nowrap" gap={0.5}>
              {Array.from({ length: 48 }, (_, i) => i + 1).map((koma) => {
                const suspense = productMap.get(koma)
                const isSelected = koma === selectedTimeCode
                const isSuspended =
                  suspense === BiddingIntradayMarketSuspenseState.SUSPENDED
                const gcState = marketKomaRibbonState(
                  now,
                  selectedDeliveryDate,
                  koma,
                )
                // 板は監視用途で GC 済コマの約定履歴も参照するため、 GC 済は
                // クリック不可にせず視覚的な減光で区別する (design
                // `intraday-price.html` の renderRibbon も gc-closed chip に
                // click handler を張る)。 入札の締切ガードは札作成側
                // (IntradayBidCreateModal / BidCreateTab) が担う。
                const isClosed = gcState === 'closed'
                // chip の配色は取引停止を最優先して warning 色にするので、
                // tooltip / aria-label でも GC 状態に加えて停止中を示す。
                const stateLabel = isSuspended
                  ? `${t(RIBBON_STATE_LABEL_KEY[gcState])} / ${t(
                      'page:biddingIntraday.market.suspended',
                    )}`
                  : t(RIBBON_STATE_LABEL_KEY[gcState])
                // 自社の入札中の札の件数 (AC-BIN-025)。 GC 済のコマでも件数が
                // あれば出す (コマの状態で隠さない)。 map に無いコマは 0 件。
                const activeOrderCount = activeOrderCountsByKoma?.get(koma) ?? 0
                // 件数を表示できるのは件数が分かっていて 1 件以上の時だけ。 未取得を
                // 「入札中の札なし」 と書くと板に残る自社の札を見落とすので、 その
                // 場合は件数セグメントごと label から落とす (コマ / 時間帯 / 状態 まで)。
                const showActiveOrderCount = activeOrderCount > 0
                const activeOrderCountLabel =
                  activeOrderCountsByKoma === undefined
                    ? undefined
                    : showActiveOrderCount
                      ? t(
                          'page:biddingIntraday.market.ribbon.activeOrderCount',
                          {
                            count: activeOrderCount,
                          },
                        )
                      : t('page:biddingIntraday.market.ribbon.noActiveOrder')
                // 番号と時間帯は全画面共通のコマ表記「コマ {番号} ({開始}〜{終了})」 (AC-BIN-025 /
                // spot-bidding.md AC-BSP-004c)。 見出しの「選択中のコマ」 を置かないので、 選んだコマの
                // 番号と時間帯はこの chip のツールチップ・読み上げで読む
                const title = [
                  formatKomaLabel(t, koma),
                  stateLabel,
                  ...(activeOrderCountLabel !== undefined
                    ? [activeOrderCountLabel]
                    : []),
                ].join(' / ')
                return (
                  <HintTooltip key={koma} content={title}>
                    {/* eslint-disable-next-line ebalance/no-adhoc-typography -- 48 コマ ribbon cell 2xs + 動的 fontWeight。板 UI の高密度 grid で design と構造非一致 = raw 許容カタログ準拠 */}
                    <Box
                      as="button"
                      alignItems="center"
                      justifyContent="center"
                      flexDirection="column"
                      display="flex"
                      minWidth="30px"
                      height="36px"
                      padding="2px 3px"
                      color={
                        isSelected
                          ? 'Text.highEmphasisInverse'
                          : isSuspended
                            ? 'Text.warningHigh'
                            : isClosed
                              ? 'Text.disabled'
                              : 'Text.midEmphasis'
                      }
                      fontSize="2xs"
                      fontWeight={
                        isSelected || gcState === 'imminent' ? 'bold' : 'normal'
                      }
                      opacity={isClosed && !isSelected ? 0.5 : 1}
                      borderWidth="1px"
                      borderColor={
                        isSelected
                          ? 'Border.accentPrimary'
                          : isSuspended
                            ? 'Border.warningMid'
                            : 'Border.lowEmphasis'
                      }
                      borderBottom={
                        ribbonBorderBottomColor(gcState) !== undefined
                          ? '2px solid'
                          : undefined
                      }
                      borderBottomColor={
                        isSelected
                          ? undefined
                          : ribbonBorderBottomColor(gcState)
                      }
                      borderRadius="sm"
                      cursor="pointer"
                      aria-label={title}
                      // 選択中のコマはこの chip だけで示す (見出しの「選択中のコマ：…」 は 2026-10-30 に削除)。
                      // 色だけに頼らず支援技術にも選択を伝える (8080 `.koma-chip.selected`)
                      aria-pressed={isSelected}
                      // 選択中は accent の地 + 白文字 (UI 辞書 koma-ribbon.md の `.selected`、
                      // 8080 `intraday-price.html` の renderRibbon)
                      backgroundColor={
                        isSelected
                          ? 'Surface.accentPrimary'
                          : isSuspended
                            ? 'Surface.warningLow'
                            : isClosed
                              ? 'Surface.tertiary'
                              : 'Surface.secondary'
                      }
                      data-gc-state={gcState}
                      data-koma={koma}
                      onClick={() => setSelectedTimeCode(koma)}
                      title={title}
                    >
                      {/* eslint-disable-next-line ebalance/no-adhoc-typography -- fontSize="inherit" fontWeight="inherit" で親 (48 コマ ribbon cell) の値を継承。板 UI の高密度 grid で design と構造非一致 = raw 許容カタログ準拠 */}
                      <Text
                        fontSize="inherit"
                        fontWeight="inherit"
                        lineHeight={1}
                      >
                        {String(koma).padStart(2, '0')}
                      </Text>
                      {/* 入札中の札の件数 (AC-BIN-025)。 0 件でも未取得でも要素は残して
                          visibility だけ落とす (= design components.css の
                          `.koma-chip-count[hidden]` と同じ意図: コマ番号の y 位置を
                          48 chip 間で揃える。 未取得のコマだけ chip の中身が上下に
                          ずれると ribbon 全体がガタつく)。 */}
                      <Text
                        marginTop="1px"
                        fontSize="inherit"
                        lineHeight={1}
                        visibility={showActiveOrderCount ? 'visible' : 'hidden'}
                        aria-hidden="true"
                        css={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {activeOrderCount}
                      </Text>
                    </Box>
                  </HintTooltip>
                )
              })}
            </Flex>
          </Box>
        </Flex>
      </Box>

      {/* コマ情報パネル (「選択中のコマ：…」 見出しと状態バッジ) と「買い入札」「売り入札」 は置かない。 AC-BIN-026b は
          欠番で、 クイック入札は 1 つ (2026-11-02 PdM 決定 #12313、 spec #12836。 2026-10-30 ユーザー指示「見出し削除、
          入札追加ボタンは 8080 準拠して配置」)。 選択中のコマと取引状態はリボンの chip (aria-pressed) とツールチップで
          読み (AC-BIN-025)、 札は板の見出しの「+ 入札追加」 (AC-BIN-027 / 028) と板の価格欄 (AC-BIN-119) から作る */}
      {/* ── 自社過不足 (AC-BIN-026a、 リボンの直下) ────
          [2026-10-13 デザイナー指示] 背を低く。 8080 `bidding/intraday-price.html` の `.im-koma-gap` と同じく
          1 行に「見出し・対象範囲・方向・必要量 → 4 セグメントの横棒 → 凡例」 を並べ、 狭い幅では凡例が次の行へ折り返す
          (旧: 見出し行 / 横棒 / 凡例行 の 3 段、 余白 12px)。 カードの余白は 8080 と同じ 8px / 12px。
          [8080 差異] 8080 は横棒を 8px にして棒の中の数値を外し、 色も方向によらず 1 色だが、 spec AC-BIN-026a が
          「割合が一定以上ならセグメント内に数値ラベル」「買方向 = 強調色 A 系 / 売方向 = 強調色 B 系」 を定めるので、
          数値が入る 16px と方向ごとの色は残す */}
      <Flex
        alignItems="center"
        flexWrap="wrap"
        rowGap={2}
        columnGap={4}
        minHeight="24px"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="md"
        backgroundColor="Surface.primary"
        paddingX={3}
        paddingY={2}
      >
        <HStack alignItems="baseline" gap={2} whiteSpace="nowrap">
          <Text textStyle="filterLabel" color="Text.highEmphasis">
            {t('page:biddingIntraday.market.gap.title')}
          </Text>
          {/* AC-BIN-103: 板発注画面は全体フィルターのエリアが無効化されるため、
              入札追加タブ (AC-BIN-012: 選択中エリア数に応じ「{N}エリア合算」) と
              異なり常に「全エリア合算」固定で表示する。 */}
          <Text color="Text.lowEmphasis" fontSize="xs">
            {t('page:biddingIntraday.market.gap.scopeAllAreas')}
          </Text>
          {/* need=0 (過不足ゼロ) のときは方向・必要量を出さず、 右の空メッセージのみ表示する
              (AC-BIN-026a)。 need 取得失敗時は過不足を判定できないので方向・必要量も出さない (#8153)。 */}
          {!boardNeedUnavailable &&
            !boardNeedLoading &&
            boardGapSegments.direction !== 'balanced' && (
              <>
                <Text
                  textStyle="filterLabel"
                  color={gapDirectionColor(boardGapSegments.direction)}
                >
                  {t(GAP_DIRECTION_LABEL_KEY[boardGapSegments.direction])}
                </Text>
                {/* 見出し行は 14px (8080 `.im-koma-gap` の font-size md) */}
                <Text color="Text.highEmphasis" fontSize="md">
                  {t('page:biddingIntraday.market.gap.needLabel', {
                    needMw: formatNumber(boardGapSegments.needAbsMw, 1),
                  })}
                </Text>
              </>
            )}
        </HStack>
        {boardNeedUnavailable ? (
          /* need 取得失敗 (= 過不足を判定できない)。 「過不足ゼロ」 と断定表示せず
             取得失敗を明示する (#8153 EN 暫定判断 2026-09-19、 PdM 事後確認は #8404)。
             入札追加タブのチャート (AC-BIN-012b) の注記と同趣旨だが、 本セクションは
             need が無いと 4 セグメントを一切描けないため「入札量のみ表示」 は付けない。 */
          <Text color="Text.midEmphasis" fontSize="xs">
            {t('page:biddingIntraday.market.gap.needUnavailable')}
          </Text>
        ) : boardNeedLoading ? (
          <Text color="Text.lowEmphasis" fontSize="xs">
            {t('page:biddingIntraday.market.gap.needLoading')}
          </Text>
        ) : boardGapSegments.direction === 'balanced' ? (
          <Text color="Text.lowEmphasis" fontSize="xs">
            {t('page:biddingIntraday.market.gap.empty')}
          </Text>
        ) : (
          <>
            {/* 4 セグメント横棒グラフ (約定 / 入札のみ (承認待ち含む) / 入札のみ (下書き等) / 未カバー)。
                各セグメント幅は need に対する割合 (AC-BIN-026a)。 hover の title と
                コンテナの aria-label の両方で全区分の数値を提供する。 未カバーは
                flex=1 で残り幅を埋め、 丸め誤差で 100% に届かない隙間を防ぐ。
                行の残り幅に伸び、 狭くても 120px は保つ (8080 `.im-koma-gap-bar` と同じ) */}
            <Flex
              flex="1 1 160px"
              overflow="hidden"
              minWidth="120px"
              height="16px"
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="full"
              aria-label={
                boardUncoveredBasisSwitched
                  ? `${boardGapBarLabel} ${boardUncoveredBasisNote}`
                  : boardGapBarLabel
              }
              backgroundColor="Surface.secondary"
              role="img"
            >
              {/* 各セグメントは幅が一定以上 (> 12%、 mock-app `IntradayMarket.razor`
                    `ComputeSelectedKomaGap` 呼び出し側の閾値と同じ) の場合だけ内部に
                    数値ラベルを表示し、 小さい場合は省略する (AC-BIN-026a)。 いずれの
                    場合も title (hover) とコンテナの aria-label で数値を提供する。 */}
              {boardGapRows.map((row) => (
                <Flex
                  key={row.key}
                  alignItems="center"
                  justifyContent="center"
                  flex={row.key === 'uncovered' ? 1 : undefined}
                  overflow="hidden"
                  width={
                    row.key === 'uncovered'
                      ? undefined
                      : `${row.pct.toFixed(2)}%`
                  }
                  title={t(row.legendKey, { value: formatNumber(row.mw, 1) })}
                  {...row.fill}
                >
                  {row.pct > 12 && (
                    // eslint-disable-next-line ebalance/no-adhoc-typography -- 4セグメント横棒グラフのセグメント内ラベル 2xs/bold (AC-BIN-026a: 幅が一定以上の場合のみ表示。48コマribbon〈同ファイル〉と同じ raw 許容カタログ準拠)
                    <Text
                      color={row.labelColor}
                      fontSize="2xs"
                      fontWeight="bold"
                      whiteSpace="nowrap"
                    >
                      {formatNumber(row.mw, 1)}
                    </Text>
                  )}
                </Flex>
              ))}
            </Flex>
            {/* 凡例 (4 区分の色分けと数値、 AC-BIN-026a)。 点は横棒の区分と同じ塗りの 8px の円
                (8080 `.im-koma-dot`)。 文字は 12px (8080 `.im-koma-gap-legend`)。
                淡い 2 区分 (下書き等・未カバー) の点は白地で見えにくいので枠線を付ける。
                「未カバー」の基準の添え書きは 未カバー の右の ? の tooltip に置く (常に読む必要の無い
                補足を小さい字の行で出していた。 2026-11-02 ユーザー指示) */}
            <HStack flexWrap="wrap" gap={4} whiteSpace="nowrap">
              {boardGapRows.map((row) => (
                <HStack key={row.key} gap={1}>
                  <Box
                    flexShrink={0}
                    width="8px"
                    height="8px"
                    borderRadius="full"
                    {...(row.key === 'bidOnlyDraft' || row.key === 'uncovered'
                      ? {
                          borderWidth: '1px',
                          borderColor: 'Border.lowEmphasis',
                        }
                      : {})}
                    {...row.fill}
                  />
                  <Text color="Text.midEmphasis" fontSize="sm">
                    {t(row.legendKey, { value: formatNumber(row.mw, 1) })}
                  </Text>
                  {row.key === 'uncovered' && (
                    <HelpIcon
                      ariaLabel={boardUncoveredBasisNote}
                      content={boardUncoveredBasisNote}
                    />
                  )}
                </HStack>
              ))}
            </HStack>
          </>
        )}
      </Flex>

      {/* 板から開く札作成 modal。 確定処理は入札追加タブと同じ hook を通す
          (GC 再判定などのガードを 2 画面に複製しない)。 */}
      <IntradayBidCreateModal
        defaultValues={quickBidDefaultValues}
        deliveryDate={selectedDeliveryDate}
        feeState={feeState}
        isOpen={
          quickBidOpen && !isReadonly && memberBidDisabledReason === undefined
        }
        onClose={() => {
          setQuickBidOpen(false)
        }}
        onSubmit={(result) =>
          memberBidDisabledReason !== undefined
            ? Promise.resolve()
            : submitQuickBid(result).then((ok) => {
                if (ok) setQuickBidOpen(false)
              })
        }
      />

      {/* ── market closed message ──────────────────────────────────── */}
      {marketClosed && (
        <Box
          alignItems="center"
          gap={3}
          display="flex"
          padding={3}
          color="Text.warningHigh"
          fontSize="md"
          borderWidth="1px"
          borderColor="Border.warningMid"
          borderRadius="md"
          backgroundColor="Surface.warningLow"
          paddingX={4}
          role="status"
        >
          <Box
            as="span"
            alignItems="center"
            flexShrink={0}
            display="inline-flex"
            aria-hidden="true"
          >
            <FaClockRegular boxSize={iconSize.labeledButton} />
          </Box>
          <Text color="Text.warningHigh" fontSize="md">
            <strong>{t('page:biddingIntraday.market.closed.title')}</strong> —{' '}
            {t('page:biddingIntraday.market.closed.body', {
              openTime: marketOpenTime,
            })}
          </Text>
        </Box>
      )}

      {/* ── 板 | 連系線空容量 → 自社入札 → 約定ティッカー (design/bidding/intraday-price.html の
            .im-market-body、 #4859)。 幅による折り返しは IntradayBoardInterconPanel の
            TICKER_LAYOUT_CSS が持つ。 板・連系線の描画は入札追加タブの参照パネルと共有する
            (AC-BIN-010 / AC-BIN-011、 IntradayBoardInterconPanel の JSDoc 参照)。
            市場未開場の減光 (dimmed) は板・連系線・ティッカーにかかり、 自社入札は操作できるまま (#10109)。 ── */}
      <IntradayBoardInterconPanel
        areaGroupFilter={
          // design `intraday-price.html` の `#im-board-group-ms`: 売|買 トグルの右に
          // 「エリア」 ラベル + 要約表示の複数選択。 幅は UI 辞書 multi-select-summary.md の
          // 板の見出しの値 (日本語 172px / 英語 240px)
          <HStack gap={2}>
            <Text textStyle="filterLabel" color="Text.highEmphasis">
              {t('page:biddingIntraday.market.board.areaGroupFilter')}
            </Text>
            <FilterMultiSelect
              width={i18n.language.startsWith('en') ? '240px' : '172px'}
              aria-label={t(
                'page:biddingIntraday.market.board.areaGroupFilterAria',
              )}
              onChange={(next) =>
                updateBoardFilters((prev) => ({
                  ...prev,
                  areaGroups: MARKET_AREA_GROUPS.filter((group) =>
                    next.has(String(group)),
                  ),
                }))
              }
              options={MARKET_AREA_GROUPS.map((group) => ({
                value: String(group),
                label: areaGroupLabel(t, group),
              }))}
              selected={new Set(boardFilters.areaGroups.map(String))}
              summary
            />
          </HStack>
        }
        areaGroups={boardAreaGroups}
        boardRows={boardRows}
        columnOrder={columnOrder}
        dimmed={marketClosed}
        headerAction={
          // 「+ 入札追加」 = クイック入札 (AC-BIN-027。 1 つだけ置く)。 置き場所は design `intraday-price.html` の
          // `#im-newbid-open-slot` (売|買 トグル・エリアの右、 M サイズ)。 出す条件は取引中かつ GC 前のコマ (AC-BIN-029)。
          // 売買は事前入力せず、 モーダルの既定の「買」 で開く (AC-BIN-028。 モーダルで変えられる)
          quickBidAvailable ? (
            <ButtonTooltip content={memberBidDisabledReason ?? readonlyReason}>
              <PrimaryButton
                disabled={!canOpenBidCreate}
                onClick={() => {
                  openQuickBid({})
                }}
                size="md"
              >
                <FaPlusRegular boxSize={iconSize.labeledButton} />
                {t('page:biddingIntraday.modal.createTitle')}
              </PrimaryButton>
            </ButtonTooltip>
          ) : undefined
        }
        interconLines={interconLines}
        interconUpdatedAt={interconUpdatedAt}
        isErrorBook={isErrorBook}
        isErrorIntercon={isErrorIntercon}
        isLoadingBook={isLoadingBook}
        isLoadingIntercon={isLoadingIntercon}
        myBids={
          <Box
            overflow="hidden"
            borderWidth="1px"
            borderColor="Border.lowEmphasis"
            borderRadius="lg"
            backgroundColor="Surface.primary"
          >
            {/* AC-BIN-127: 要確認の警告は自社入札パネルの先頭に全幅で置く (design intraday-price.html `.im-mybids`)。 */}
            <IntradayNeedsReviewBanner count={needsReviewCount} />
            {/* パネルの見出しの地は primary (8080 `.im-panel-header`)。 右端にエリア・売/買の絞り込み
                (AC-BIN-103、 8080 `#im-mybids-header` の `.filter-group`: 見出しの右に寄せ、 狭い時は折り返す) */}
            <Flex
              alignItems="center"
              flexWrap="wrap"
              gap={3}
              // 絞り込みは S (32px) でも、 帯の高さは板の見出しにそろえる (MARKET_PANEL_HEADER_MIN_HEIGHT)
              minHeight={MARKET_PANEL_HEADER_MIN_HEIGHT}
              padding="0.4rem 0.65rem"
              borderColor="Border.lowEmphasis"
              borderBottomWidth="1px"
              backgroundColor="Surface.primary"
            >
              <Text textStyle="subSectionTitle" color="Text.highEmphasis">
                {t('page:biddingIntraday.market.myBids.title', {
                  koma: selectedTimeCode,
                  count: shownMyBidEntries.length,
                })}
              </Text>
              <HStack flexWrap="wrap" flexShrink={0} gap={3} marginLeft="auto">
                <Text textStyle="filterLabel" color="Text.highEmphasis">
                  {t('page:biddingIntraday.area.label')}
                </Text>
                {/* 幅は 8080 `#im-mybids-area-ms .ecl-multi-select` (日本語 140px / 英語 160px) */}
                <FilterMultiSelect
                  width={i18n.language.startsWith('en') ? '160px' : '140px'}
                  aria-label={t(
                    'page:biddingIntraday.market.myBids.areaFilterAria',
                  )}
                  onChange={(next) =>
                    updateMyBidsFilters((prev) => ({
                      ...prev,
                      areas: myBidsAreaOptions.filter((area) =>
                        next.has(String(area)),
                      ),
                    }))
                  }
                  options={myBidsAreaOptions.map((area) => ({
                    value: String(area),
                    label: convertAreaLabel(area),
                  }))}
                  selected={new Set(effectiveMyBidsFilters.areas.map(String))}
                  // カードの見出しの右に置く絞り込みは S (32px) で見出しを低く保つ
                  // (UI 辞書 card.md「見出しの右端に置く部品の大きさ」、 2026-11-02 ユーザー指示)
                  size="sm"
                  summary
                />
                <Text textStyle="filterLabel" color="Text.highEmphasis">
                  {t('page:biddingIntraday.history.sideLabel')}
                </Text>
                <TradeSideRadioGroup
                  allLabel={t('page:biddingIntraday.history.sideAll')}
                  ariaLabel={t(
                    'page:biddingIntraday.market.myBids.sideFilterAria',
                  )}
                  name="intraday-board-my-bids-side"
                  onChange={(next) =>
                    updateMyBidsFilters((prev) => ({ ...prev, side: next }))
                  }
                  value={myBidsFilters.side}
                  values={{ all: 'all', buy: 'buy', sell: 'sell' }}
                />
              </HStack>
            </Flex>
            <Box ref={myBidsWrapRef} overflowX="auto">
              {/* 自前 <table> から DS Table 部品へ置換 (2026-08-27 デザイナー指摘:
              全入札一覧は 8080 同様 DS table にする)。 zebra / 罫線 / ヘッダ背景は
              DS recipe が持つため、 個別の inline style を落とす。
              揃えは列ごとの定義 (col.align) を TH と TD の両方へ渡し、
              位置ベースの分岐で TH だけがずれる形を作らない。 */}
              <TableRoot>
                <TableHeader>
                  <TableRow>
                    {MY_BIDS_COLUMNS.map((col) => (
                      <TableColumnHeader
                        key={col.key}
                        textStyle="tableHeader"
                        textAlign={col.align}
                        // 操作列は右端に固定する (狭い器で横スクロールしても見える。
                        // 共通 hook / UI 辞書 table.md §1)。
                        {...(col.key === 'actions'
                          ? {
                              'data-sticky-shadow': myBidsStickyShadow,
                              style: stickyActionHeaderStyle(TABLE_HEADER_BG),
                            }
                          : {})}
                      >
                        {t(col.labelKey)}
                      </TableColumnHeader>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shownMyBidEntries.length === 0 ? (
                    <TableRow>
                      <TableCell
                        color="Text.midEmphasis"
                        textAlign="center"
                        colSpan={MY_BIDS_COLUMNS.length}
                        paddingY={6}
                      >
                        {/* 絞り込みで 0 件になった時は「入札が無い」 と分ける (このコマに入札があるのに見えない
                            理由を出す。 8080 `renderMyBids` と同じ) */}
                        {myBids.length === 0
                          ? t('page:biddingIntraday.market.myBids.empty')
                          : t(
                              'page:biddingIntraday.market.myBids.emptyFiltered',
                            )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    shownMyBidEntries.map(
                      ({ order: bid, row, actionFlags }) => (
                        <TableRow key={bid.id}>
                          <TableCell textStyle="tableCell" textAlign="left">
                            <IntradayBidStatusCell bid={row} />
                            {/* AC-BIN-127: 入札一覧と同じ「要確認」の印 (design intraday-price.html `_needsReviewChip`)。 */}
                            <IntradayNeedsReviewChip
                              needsReview={row.needsReview}
                            />
                          </TableCell>
                          <TableCell textStyle="tableCell" textAlign="center">
                            <TradeSideLabel side={bid.direction} />
                          </TableCell>
                          <TableCell textStyle="tableCell" textAlign="left">
                            {convertAreaLabel(bid.areaCode) || '—'}
                          </TableCell>
                          <TableCell
                            textStyle="tableCell"
                            textAlign="right"
                            css={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            {formatNumber(decimalVolumeToMw(bid.volumeMwh))}
                          </TableCell>
                          <TableCell
                            textStyle="tableCell"
                            textAlign="right"
                            css={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            {formatDecimalValue(bid.priceYenPerKwh?.value, 2)}
                          </TableCell>
                          <TableCell
                            textStyle="tableCell"
                            textAlign="right"
                            css={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            {formatNumber(
                              decimalVolumeToMw(bid.matchedVolumeMwh),
                            )}
                          </TableCell>
                          <TableCell
                            textStyle="tableCell"
                            textAlign="right"
                            css={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            {bid.matchedPriceYenPerKwh !== undefined
                              ? formatDecimalValue(
                                  bid.matchedPriceYenPerKwh.value,
                                  2,
                                )
                              : '—'}
                          </TableCell>
                          <TableCell
                            textStyle="tableCell"
                            textAlign="right"
                            whiteSpace="nowrap"
                            data-sticky-action
                            data-sticky-shadow={myBidsStickyShadow}
                            // 縞の無い表なので全行 index 0 (= 地は白)。
                            style={stickyActionCellStyle(0)}
                          >
                            {/* 置くボタンが無い行は入札一覧と同じく「—」。 */}
                            {actionFlags.edit ||
                            actionFlags.withdraw ||
                            actionFlags.returnToDraft ? (
                              <IntradayBidRowActions
                                row={row}
                                flags={actionFlags}
                                isReadonly={isReadonly}
                                isReturningToDraft={returnToDraft.isReturning}
                                onEdit={() => {
                                  if (!isReadonly) setDetailOrder(bid)
                                }}
                                onReturnToDraft={returnToDraft.returnToDraft}
                                onWithdraw={withdrawConfirm.open}
                              />
                            ) : (
                              <Text as="span" color="Text.lowEmphasis">
                                —
                              </Text>
                            )}
                          </TableCell>
                        </TableRow>
                      ),
                    )
                  )}
                </TableBody>
              </TableRoot>
            </Box>
          </Box>
        }
        onPriceCellBid={
          canBidFromPriceCell
            ? ({ side, price }: PriceCellBid) => {
                openQuickBid({
                  side: side === 'buy' ? BUY_SIDE : SELL_SIDE,
                  priceYenPerKwh: price,
                })
              }
            : undefined
        }
        priceCellDisabledReason={
          memberBidDisabledReason ?? readonlyReason ?? noIssuerReason
        }
        streamStopped={streamStopped}
        ticker={tickerPanel}
        tradeSummary={{
          summary: boardTradeSummary,
          isLoading: isLoadingContracts,
          isError: isErrorContractsWithoutData,
        }}
      />

      {/* 自社入札の取消確認 (AC-008) / 詳細・編集 (AC-007)。 入札一覧と同じもの (#10110)。 */}
      <IntradayWithdrawConfirmDialog confirm={withdrawConfirm} />
      {detailOrder !== undefined && (
        <IntradayOrderDetailModal
          row={detailOrder}
          feeState={feeState}
          isOpen
          onClose={() => setDetailOrder(undefined)}
          orders={allOrders}
        />
      )}
    </VStack>
  )
}
